
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { signInWithPopup, signInWithEmailAndPassword, createUserWithEmailAndPassword, sendEmailVerification, GoogleAuthProvider } from 'firebase/auth';
import { auth, googleProvider } from '../firebase';
import { Hash, Mail, Lock, Eye, EyeOff, ArrowRight, Loader2, ChevronLeft } from 'lucide-react';
import { useTranslation } from '../lib/translations';

const Login: React.FC = () => {
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { t } = useTranslation();

  const handleGoogleLogin = async () => {
    try { 
      const result = await signInWithPopup(auth, googleProvider);
      const credential = GoogleAuthProvider.credentialFromResult(result);
      if (credential?.accessToken) {
        sessionStorage.setItem('google_access_token', credential.accessToken);
      }
    } catch (error) { 
      console.error(error);
      setError(t.login.authFailed); 
    }
  };

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault(); setLoading(true); setError('');
    try {
      if (isSignUp) {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        await sendEmailVerification(userCredential.user);
      } else { await signInWithEmailAndPassword(auth, email, password); }
    } catch (err: any) { setError(t.login.authFailed); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 relative overflow-hidden px-4">
      <Link to="/" className="absolute top-6 left-6 flex items-center gap-2 text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors z-50 text-xs font-black uppercase tracking-widest">
        <ChevronLeft size={16} /> {t.login.home}
      </Link>
      <div className="relative z-10 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex p-3 bg-purple-600 rounded-lg mb-4 shadow-xl shadow-purple-900/20"><Hash size={32} className="text-white" /></div>
          <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">SharpNote</h1>
          <p className="text-slate-500 text-sm font-medium">{t.login.tagline}</p>
        </div>
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 p-8 rounded-lg shadow-2xl">
          <div className="flex bg-slate-100 dark:bg-slate-950/50 p-1 rounded-md mb-8 border border-slate-200 dark:border-slate-800">
            <button onClick={() => setIsSignUp(false)} className={`flex-1 py-2 rounded-md text-xs font-black uppercase tracking-widest transition-all ${!isSignUp ? 'bg-white dark:bg-slate-800 text-slate-900 dark:text-white shadow-sm' : 'text-slate-400'}`}>{t.login.signIn}</button>
            <button onClick={() => setIsSignUp(true)} className={`flex-1 py-2 rounded-md text-xs font-black uppercase tracking-widest transition-all ${isSignUp ? 'bg-white dark:bg-slate-800 text-slate-900 dark:text-white shadow-sm' : 'text-slate-400'}`}>{t.login.register}</button>
          </div>
          <form onSubmit={handleEmailAuth} className="space-y-4">
            <div className="space-y-1"><label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">{t.login.email}</label>
              <div className="relative"><Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={16} /><input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg py-3 pl-10 pr-4 text-sm focus:border-purple-500 outline-none" /></div>
            </div>
            <div className="space-y-1"><label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">{t.login.password}</label>
              <div className="relative"><Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={16} /><input type={showPassword ? 'text' : 'password'} required value={password} onChange={(e) => setPassword(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg py-3 px-10 text-sm focus:border-purple-500 outline-none" /><button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">{showPassword ? <EyeOff size={16} /> : <Eye size={16} />}</button></div>
            </div>
            {error && <p className="text-red-500 text-xs font-bold bg-red-50 dark:bg-red-500/10 p-2.5 rounded border border-red-100 dark:border-red-500/20">{error}</p>}
            <button type="submit" disabled={loading} className="w-full bg-purple-600 text-white py-3 rounded-lg font-bold shadow-md flex items-center justify-center gap-2 hover:bg-purple-700 disabled:opacity-50">{loading ? <Loader2 className="animate-spin" size={18} /> : <>{isSignUp ? t.login.createAccount : t.login.authenticate} <ArrowRight size={16} /></>}</button>
          </form>
          <div className="my-6 flex items-center gap-3"><div className="flex-1 h-px bg-slate-100 dark:bg-slate-800"></div><span className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em]">{t.login.socialIntegration}</span><div className="flex-1 h-px bg-slate-100 dark:bg-slate-800"></div></div>
          <button onClick={handleGoogleLogin} className="w-full flex items-center justify-center gap-3 bg-white border border-slate-200 dark:border-transparent dark:bg-slate-800 text-slate-900 dark:text-white py-3 rounded-lg font-bold text-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-all">
            <img src="https://axelayi.github.io/bass-arena-assets/images/google-logo.png" alt="Google" className="w-5 h-5 object-contain" /> {t.login.googleSync}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Login;
